/* ============================================================
   GW · Home
   Revelado al entrar en pantalla y arrastre de los carruseles.
   Se ejecuta también al re-renderizar una sección en el editor.
   ============================================================ */

(function () {
  const menosMovimiento = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Revelado ---------- */

  let observador = null;

  function iniciarRevelado(raiz) {
    const elementos = (raiz || document).querySelectorAll('.gw-revela:not(.visible)');
    if (!elementos.length) return;

    if (menosMovimiento || !('IntersectionObserver' in window)) {
      elementos.forEach((el) => el.classList.add('visible'));
      return;
    }

    if (!observador) {
      observador = new IntersectionObserver(
        (entradas) => {
          entradas.forEach((entrada) => {
            if (!entrada.isIntersecting) return;
            entrada.target.classList.add('visible');
            observador.unobserve(entrada.target);
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
      );
    }

    elementos.forEach((el) => observador.observe(el));
  }

  /* ---------- Arrastre de carruseles ---------- */

  function iniciarArrastre(raiz) {
    (raiz || document).querySelectorAll('[data-gw-arrastra]:not([data-listo])').forEach((pista) => {
      pista.dataset.listo = 'si';

      let activo = false;
      let inicioX = 0;
      let inicioScroll = 0;
      let movido = 0;

      pista.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse') return;
        activo = true;
        movido = 0;
        inicioX = e.clientX;
        inicioScroll = pista.scrollLeft;
        pista.classList.add('arrastrando');
        pista.setPointerCapture(e.pointerId);
      });

      pista.addEventListener('pointermove', (e) => {
        if (!activo) return;
        const delta = e.clientX - inicioX;
        movido = Math.abs(delta);
        pista.scrollLeft = inicioScroll - delta;
      });

      function soltar(e) {
        if (!activo) return;
        activo = false;
        pista.classList.remove('arrastrando');
        if (e.pointerId != null && pista.hasPointerCapture(e.pointerId)) {
          pista.releasePointerCapture(e.pointerId);
        }
        // Si se ha arrastrado, no dejamos que el clic abra el enlace
        if (movido > 6) {
          const bloquea = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
          pista.addEventListener('click', bloquea, { capture: true, once: true });
          window.setTimeout(() => pista.removeEventListener('click', bloquea, true), 60);
        }
      }

      pista.addEventListener('pointerup', soltar);
      pista.addEventListener('pointercancel', soltar);
      pista.addEventListener('dragstart', (e) => e.preventDefault());
    });
  }

  /* ---------- Carrusel: flechas y progreso ---------- */

  function iniciarCarrusel(raiz) {
    (raiz || document).querySelectorAll('[data-gwp-carrusel]:not([data-carrusel-listo])').forEach((zona) => {
      zona.dataset.carruselListo = 'si';

      const pista = zona.querySelector('[data-gwp-pista]');
      if (!pista) return;

      const barra = zona.querySelector('[data-gwp-barra]');
      const ventanas = zona.querySelector('[data-gwp-ventanas]');
      const anterior = zona.querySelector('[data-gwp-prev]');
      const siguiente = zona.querySelector('[data-gwp-next]');

      /* Cuánto avanza cada golpe de flecha: una tarjeta */
      function paso() {
        const item = pista.querySelector('.gwp__item');
        if (!item) return pista.clientWidth;
        const hueco = parseFloat(getComputedStyle(pista).columnGap) || 0;
        return item.getBoundingClientRect().width + hueco;
      }

      /* Cuántas "ventanas" caben: una por pantalla de carrusel */
      function cuantasVentanas() {
        if (pista.clientWidth <= 0) return 1;
        return Math.max(1, Math.ceil(pista.scrollWidth / pista.clientWidth));
      }

      function pintarVentanas() {
        if (!ventanas) return;
        const total = cuantasVentanas();
        if (ventanas.childElementCount === total) return;

        ventanas.innerHTML = '';
        for (let i = 0; i < total; i++) {
          const b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('aria-label', 'Ir al grupo ' + (i + 1));
          b.addEventListener('click', () => {
            pista.scrollTo({ left: i * pista.clientWidth, behavior: 'smooth' });
          });
          ventanas.appendChild(b);
        }
      }

      function actualizar() {
        const recorrido = pista.scrollWidth - pista.clientWidth;
        const avance = recorrido > 0 ? pista.scrollLeft / recorrido : 0;

        if (barra) {
          const visible = pista.scrollWidth > 0 ? pista.clientWidth / pista.scrollWidth : 1;
          barra.style.setProperty('--w', Math.min(visible, 1) * 100 + '%');
          barra.style.setProperty('--p', avance);
        }

        if (ventanas && ventanas.children.length) {
          const actual = Math.round(pista.scrollLeft / Math.max(pista.clientWidth, 1));
          Array.from(ventanas.children).forEach((b, i) => {
            b.classList.toggle('activa', i === Math.min(actual, ventanas.children.length - 1));
          });
        }

        if (anterior) anterior.disabled = pista.scrollLeft <= 2;
        if (siguiente) siguiente.disabled = pista.scrollLeft >= recorrido - 2;
      }

      anterior?.addEventListener('click', () => pista.scrollBy({ left: -paso(), behavior: 'smooth' }));
      siguiente?.addEventListener('click', () => pista.scrollBy({ left: paso(), behavior: 'smooth' }));

      pista.addEventListener('scroll', actualizar, { passive: true });
      window.addEventListener('resize', () => { pintarVentanas(); actualizar(); });

      pintarVentanas();
      actualizar();
    });
  }

  /* ---------- Favoritos (se guardan en el navegador) ---------- */

  const CLAVE = 'gw-favoritos';

  function leerFavoritos() {
    try {
      return JSON.parse(window.localStorage.getItem(CLAVE) || '[]');
    } catch (e) {
      return [];
    }
  }

  function guardarFavoritos(lista) {
    try {
      window.localStorage.setItem(CLAVE, JSON.stringify(lista));
    } catch (e) {
      /* modo privado o almacenamiento lleno: no pasa nada */
    }
  }

  function iniciarFavoritos(raiz) {
    const botones = (raiz || document).querySelectorAll('[data-gw-favorito]:not([data-fav-listo])');
    if (!botones.length) return;

    const guardados = leerFavoritos();

    botones.forEach((btn) => {
      btn.dataset.favListo = 'si';
      const id = btn.dataset.gwFavorito;
      if (guardados.indexOf(id) !== -1) btn.setAttribute('aria-pressed', 'true');

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const lista = leerFavoritos();
        const puesto = lista.indexOf(id);

        if (puesto === -1) {
          lista.push(id);
          btn.setAttribute('aria-pressed', 'true');
        } else {
          lista.splice(puesto, 1);
          btn.setAttribute('aria-pressed', 'false');
        }

        guardarFavoritos(lista);
      });
    });
  }

  /* ---------- Vídeo: solo se reproduce mientras se ve ---------- */

  function iniciarVideo(raiz) {
    (raiz || document).querySelectorAll('[data-gw-video]:not([data-listo])').forEach((zona) => {
      zona.dataset.listo = 'si';

      const video = zona.querySelector('video');
      if (!video) return;

      video.muted = true;
      video.play().catch(() => {});

      if ('IntersectionObserver' in window) {
        const obs = new IntersectionObserver(
          (entradas) => {
            entradas.forEach((e) => {
              if (e.isIntersecting) video.play().catch(() => {});
              else video.pause();
            });
          },
          { threshold: 0.2 }
        );
        obs.observe(video);
      }

      const boton = zona.querySelector('[data-gw-sonido]');
      if (!boton) return;

      boton.addEventListener('click', () => {
        video.muted = !video.muted;
        boton.setAttribute('aria-pressed', String(!video.muted));
        if (!video.muted) video.play().catch(() => {});
      });
    });
  }

  /* ---------- Arranque ---------- */

  function iniciar(raiz) {
    iniciarRevelado(raiz);
    iniciarArrastre(raiz);
    iniciarCarrusel(raiz);
    iniciarFavoritos(raiz);
    iniciarVideo(raiz);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => iniciar());
  } else {
    iniciar();
  }

  // El editor de temas vuelve a pintar secciones sueltas
  document.addEventListener('shopify:section:load', (e) => iniciar(e.target));
})();
